import { z } from 'zod';
import { resolveDisplayKey, translateErrorKey } from './contract';
import { toUserFacingMessage } from './types';
import type { ResolvedApiError, UserFacingMessage } from './types';

/**
 * Bulk-operation outcome classification (ADR-5). One shared implementation, consumed
 * identically by `legacy/hooks/useGenericMutation.ts` and
 * `features/end-users/tags-nfc/hooks/tags.ts::useCreateTag` — both call sites drop their
 * independent `status === 207` / `status === 207 || status === 400` shape-sniffing in
 * favor of `resolveBulkSuccess`/`resolveBulkError` returning `undefined` for a non-bulk
 * payload.
 */
export const BULK_OUTCOME = {
	AllSucceeded: 'all-succeeded',
	PartialSuccess: 'partial-success',
	AllExist: 'all-exist',
	AllFailed: 'all-failed',
	Unknown: 'unknown',
} as const;

export type BulkOutcome = (typeof BULK_OUTCOME)[keyof typeof BULK_OUTCOME];

/**
 * Backend-owned key identifying an "all failed" bulk outcome (observed at HTTP 404).
 * Checked BEFORE any structural rule (key-first — ADR-5's outcome table row 1): a 404
 * all-failed response is never misclassified by shape alone, even if it happens to carry
 * non-empty `succeeded`/`already_exist` arrays.
 */
export const BULK_ALL_FAILED_KEY = 'internal.bulk_operation.all_failed' as const;

// Per-item key field name — assumption: the design specifies each `failed[]`/
// `already_exist[]` entry carries "its own key (or equivalent field)" without nailing
// down the exact name. Using `key`, matching the top-level envelope field.
const bulkFailureSchema = z.object({
	id: z.union([z.string(), z.number()]),
	key: z.string().optional(),
	status: z.number().optional(),
	detail: z.unknown().optional(),
});

export const bulkDetailSchema = z.object({
	succeeded: z.array(z.unknown()).optional(),
	failed: z.array(bulkFailureSchema).optional(),
	// `already_exist` (singular) is the first-class field per the design. The plural
	// `already_exists` alias below is tolerated ONLY because the exact spelling is
	// unverified against a real captured backend payload. Do NOT delete this fallback here
	// — removal is deferred to this multi-PR change's final catalog-migration phase (a
	// later PR, gated on the backend supplying a real payload to confirm which spelling
	// it actually sends), tracked as `sdd/adapt-api-error-contract` task 5.4 in Engram.
	already_exist: z.array(bulkFailureSchema).optional(),
	already_exists: z.array(bulkFailureSchema).optional(),
});

export type BulkDetail = z.infer<typeof bulkDetailSchema>;
type BulkFailureItem = z.infer<typeof bulkFailureSchema>;

export interface BulkItemFailure {
	readonly id: string | number;
	readonly key?: string;
	readonly message: UserFacingMessage;
}

export interface BulkFeedback {
	readonly outcome: BulkOutcome;
	readonly tone: 'success' | 'warning' | 'error';
	/** Fully composed + translated summary — the only thing call sites render. */
	readonly message: UserFacingMessage;
	readonly succeededCount: number;
	readonly failed: readonly BulkItemFailure[];
	readonly alreadyExisting: readonly BulkItemFailure[];
}

/**
 * Translates a per-item key through the SAME whitelist + fallback path as top-level keys
 * (Q6) — an unrecognized item key (or a missing one) degrades to generic copy, never a
 * raw `Missing: <key>` string rendered to the user.
 */
const toItemFailure = (item: BulkFailureItem): BulkItemFailure => ({
	id: item.id,
	key: item.key,
	message: toUserFacingMessage(
		translateErrorKey(resolveDisplayKey(item.key) ?? 'unexpected_error'),
	),
});

const formatItemList = (items: readonly BulkItemFailure[]): string =>
	items.map((item) => `#${item.id} — ${item.message.text}`).join('\n');

/** True only when at least one of the three bulk arrays is present (even if empty). */
const isBulkShapedDetail = (detail: BulkDetail): boolean =>
	detail.succeeded !== undefined ||
	detail.failed !== undefined ||
	detail.already_exist !== undefined ||
	detail.already_exists !== undefined;

const composeMessage = (
	outcome: BulkOutcome,
	succeededCount: number,
	failed: readonly BulkItemFailure[],
	alreadyExisting: readonly BulkItemFailure[],
): UserFacingMessage => {
	switch (outcome) {
		case BULK_OUTCOME.PartialSuccess: {
			let text = translateErrorKey('partial_success');
			if (failed.length > 0)
				text += `\n❌ ${translateErrorKey('failed')}: ${formatItemList(failed)}`;
			if (alreadyExisting.length > 0)
				text += `\n⚠️ ${translateErrorKey('already_exists')}: ${formatItemList(alreadyExisting)}`;
			text += `\n✅ ${translateErrorKey('succeeded')}: ${succeededCount}`;
			return toUserFacingMessage(text);
		}
		case BULK_OUTCOME.AllExist:
			return toUserFacingMessage(
				`${translateErrorKey('all_exist')}: ${formatItemList(alreadyExisting)}`,
			);
		case BULK_OUTCOME.AllFailed:
			return toUserFacingMessage(`${translateErrorKey('all_failed')}: ${formatItemList(failed)}`);
		case BULK_OUTCOME.AllSucceeded:
			return toUserFacingMessage(`${translateErrorKey('succeeded')}: ${succeededCount}`);
		case BULK_OUTCOME.Unknown:
		default:
			return toUserFacingMessage(translateErrorKey('unexpected_error'));
	}
};

const TONE_BY_OUTCOME: Record<BulkOutcome, BulkFeedback['tone']> = {
	[BULK_OUTCOME.AllSucceeded]: 'success',
	[BULK_OUTCOME.PartialSuccess]: 'warning',
	[BULK_OUTCOME.AllExist]: 'warning',
	[BULK_OUTCOME.AllFailed]: 'error',
	[BULK_OUTCOME.Unknown]: 'error',
};

interface DeriveOutcomeInput {
	readonly hasSucceeded: boolean;
	readonly hasFailed: boolean;
	readonly hasAlreadyExisting: boolean;
	readonly key: string | undefined;
}

/**
 * Outcome derivation — KEY FIRST, structure second (ADR-5). `key === BULK_ALL_FAILED_KEY`
 * short-circuits to `AllFailed` before any structural rule runs; only then does the
 * succeeded/failed/already-existing combination table decide. Named-field input (not
 * positional booleans) so a future edit can't silently transpose two adjacent flags.
 */
const deriveOutcome = ({
	hasSucceeded,
	hasFailed,
	hasAlreadyExisting,
	key,
}: DeriveOutcomeInput): BulkOutcome => {
	if (key === BULK_ALL_FAILED_KEY) return BULK_OUTCOME.AllFailed;
	if (hasSucceeded && (hasFailed || hasAlreadyExisting)) return BULK_OUTCOME.PartialSuccess;
	if (!hasSucceeded && hasAlreadyExisting && !hasFailed) return BULK_OUTCOME.AllExist;
	if (!hasSucceeded && hasFailed) return BULK_OUTCOME.AllFailed;
	if (hasSucceeded && !hasFailed && !hasAlreadyExisting) return BULK_OUTCOME.AllSucceeded;
	return BULK_OUTCOME.Unknown;
};

const buildFeedback = (detail: BulkDetail, key?: string): BulkFeedback => {
	const succeededCount = (detail.succeeded ?? []).length;
	const failed = (detail.failed ?? []).map(toItemFailure);
	const alreadyExisting = (detail.already_exist ?? detail.already_exists ?? []).map(toItemFailure);

	const outcome = deriveOutcome({
		hasSucceeded: succeededCount > 0,
		hasFailed: failed.length > 0,
		hasAlreadyExisting: alreadyExisting.length > 0,
		key,
	});

	return {
		outcome,
		tone: TONE_BY_OUTCOME[outcome],
		message: composeMessage(outcome, succeededCount, failed, alreadyExisting),
		succeededCount,
		failed,
		alreadyExisting,
	};
};

const successEnvelopeSchema = z.object({ detail: z.unknown().optional() });

/**
 * Resolves a SUCCESS response body (e.g. HTTP 207) into `BulkFeedback`. Returns
 * `undefined` when the payload isn't bulk-shaped — this is what lets call sites drop
 * their `status === 207` sniffing entirely.
 */
export const resolveBulkSuccess = (data: unknown): BulkFeedback | undefined => {
	const envelope = successEnvelopeSchema.safeParse(data);
	if (!envelope.success) return undefined;

	const parsedDetail = bulkDetailSchema.safeParse(envelope.data.detail);
	if (!parsedDetail.success || !isBulkShapedDetail(parsedDetail.data)) return undefined;

	return buildFeedback(parsedDetail.data);
};

/**
 * Resolves an already-`resolveApiError`-resolved error into `BulkFeedback`. Returns
 * `undefined` when `resolved.detail` isn't bulk-shaped — this is what lets call sites
 * drop their `isBulk && (status === 207 || status === 400)` shape-sniffing entirely.
 *
 * `Known limitation:` `resolved.detail` comes from `resolveApiError`'s stage-1 success
 * path only when the body carries a top-level `key` (`apiErrorBodySchema` requires it).
 * A bulk-shaped error body arriving WITHOUT a `key` would fail stage 1, and stage 2/3's
 * `extractLegacyDetail` doesn't recognize an object `detail` as a candidate — it degrades
 * to `resolved.detail: undefined`, so this function would then see a non-bulk-shaped
 * `undefined` and fall back to the caller's generic error toast, losing the granular
 * per-item breakdown. Accepted: the backend's documented contract states every bulk
 * outcome (207 partial and the 404 all-failed case) always carries `key` — "mismo
 * envelope en todos los casos" — so this path is not expected to be reachable in
 * practice. Not fixed here because the root cause lives in `resolveApiError.ts`'s
 * stage 2/3 (an earlier, already-shipped PR in this chain), out of this module's scope.
 */
export const resolveBulkError = (resolved: ResolvedApiError): BulkFeedback | undefined => {
	const parsedDetail = bulkDetailSchema.safeParse(resolved.detail);
	if (!parsedDetail.success || !isBulkShapedDetail(parsedDetail.data)) return undefined;

	return buildFeedback(parsedDetail.data, resolved.key);
};
